import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import api from "@/services/api";
import SelectUser from "@/components/selectUser";

// Pilotage — PNL et Tréso 2027, Monte Carlo. Tout le plan est une liste plate de lignes
// { name, type, months } où months porte une valeur k€ par mois : créances, budget signé, deals,
// paie, frais, contentieux. Pas un agrégat, pas un palier implicite, pas une hypothèse globale —
// la seule incertitude est la proba portée par une ligne (une créance rentre ou pas, un deal est
// gagné ou pas, une autorisation de licenciement est refusée ou pas).
// La vue est orientée BU : elle suit le sélecteur global du header (redux Auth.businessUnit).
// Selego a son plan saisi à la main côté API ; les autres BU (Tirana) sont assemblées
// entièrement depuis le réel accounting (people générés depuis les contrats de la BU).
// "All Teams" retombe sur Selego : les plans ne s'additionnent pas.
// Le plan vit côté API (controllers/pilotage.js, en code — un commit = une révision) : POST
// /pilotage/data renvoie tout assemblé — lignes saisies à la main, budgets live en budget/pipe,
// coûts contrats appliqués au people. Les modifications à l'écran sont des simulations, perdues
// au refresh — SAUF si la ligne porte un bouton Sauver (budget, pipe, people, frais fixes) : lui
// écrit dans la source (phasage/confiance du budget, périodes de contrat, frais fixes de la BU),
// puis la page recharge le réel.

// Fenêtre GLISSANTE : 17 mois à partir du MOIS PROCHAIN. Même règle exactement que côté API
// (controllers/pilotage.js) — les deux la recalculent, elles tombent donc sur les mêmes mois.
// Elle était figée à août 26, le moteur rejouait des mois déjà passés. Le mois courant n'est pas
// une colonne : cash0 est le solde bancaire d'aujourd'hui, il porte déjà ce qui est sorti depuis
// le 1er. Ce qu'il reste à payer sur le mois en cours est dans les arriérés.
const N = 17;
const MONTH_LABELS = ["Janv", "Févr", "Mars", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];
const now = new Date();
const KEYS = Array.from({ length: N }, (_, i) => {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
});
const label = (k) => {
  const [y, m] = k.split("-");
  return `${MONTH_LABELS[Number(m) - 1]} ${y.slice(2)}`;
};
const MONTHS = KEYS.map(label);

// Deux grilles, pas une. Exclure le mois courant est une règle de CASH : cash0 est le solde du
// jour, octobre en colonne 0 repaierait des salaires déjà sortis. Ce n'est pas une règle de P&L —
// un P&L n'a ni solde de départ ni double compte, et octobre y est un mois comme un autre. On
// lisait donc un P&L qui commençait au mois prochain, et un deal daté du mois courant disparaissait
// du pipe le 1er : la ligne n'était pas mise à zéro, elle n'était plus affichée du tout.
// DATA_KEYS porte les montants à partir du mois COURANT, KEYS reste la fenêtre de cash et ancre
// seule simulate(), les arriérés et les créances — le moteur Monte Carlo ne bouge pas d'un chiffre.
const DATA_KEYS = KEYS;
const DATA_MONTHS = DATA_KEYS.map(label);
// Deux objets figés plutôt que construits au rendu : ils servent de valeur de contexte, et un
// littéral recréé à chaque rendu ferait re-rendre toutes les lignes pour rien.
const COLS_PNL = { keys: DATA_KEYS, months: DATA_MONTHS };
const COLS_TRESO = { keys: KEYS, months: MONTHS };
const colsFor = (tab) => (tab === "pnl" ? COLS_PNL : COLS_TRESO);

// L'onglet décide des colonnes, et une dizaine de composants de ligne les lisent. Un contexte
// plutôt qu'un prop traversé partout : l'alternative était d'ajouter `cols` à chaque appel de
// chaque rangée, et une seule oubliée décale une section d'un mois sans que ça se voie.
const ColsContext = React.createContext(colsFor("treso"));
const useCols = () => React.useContext(ColsContext);

const DRAWS = 3000;

const COLORS = { accent: "#0E7C7B", good: "#2E7D32", bad: "#C62828", warn: "#B26A00", violet: "#6D28D9", muted: "#6B7280", line: "#E5E7EB" };

const num = (v) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : 0;
};

function normalize(raw) {
  const S = JSON.parse(JSON.stringify(raw));
  ["cash0", "cspCost", "rcMonths", "protDelay", "protRefuse"].forEach((k) => (S[k] = num(S[k])));
  S.lines = S.lines.map((l) => {
    const months = {};
    // Sur DATA_KEYS : simulate() ne lit que KEYS, mais la table affiche le mois courant et une
    // case non normalisée se saisirait sur un undefined.
    DATA_KEYS.forEach((k) => (months[k] = num(l.months?.[k])));
    return { ...l, months, exit: num(l.exit), p: num(l.p) };
  });
  return S;
}

/* ---------- engine ---------- */
function makeRng(seed) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

// One-time RC = rcMonths × salaire au mois de sortie (fallback : premier mois payé).
const salaryAt = (l, m) => l.months[KEYS[Math.max(0, Math.min(m, N - 1))]] || KEYS.map((k) => l.months[k]).find((v) => v > 0) || 0;

// Pas de « Garder » : rester n'est pas une décision, c'est ce que disent les cases. Le statut ne
// sert qu'à déclarer une sortie — vide (ou tout autre valeur) = la personne reste, on paie ce qui est saisi.
const EXITS = ["rc", "eco", "gone"];

// Exit month of a CDI for one draw. Statut vide = reste ; tirage de refus pour un protégé seul.
function cdiExit(l, S, rng) {
  if (!EXITS.includes(l.st)) return { em: N + 9, oneT: 0 };
  if (l.st === "gone") return { em: l.exit, oneT: 0 };
  if (l.st === "rc") {
    const em = l.exit + (l.prot ? Math.max(0, S.protDelay - 1) : 0);
    if (l.prot && rng() < S.protRefuse / 100) return { em: N + 9, oneT: 0 };
    return { em, oneT: salaryAt(l, l.exit) * S.rcMonths };
  }
  // eco (CSP)
  const em = l.exit + (l.prot ? S.protDelay : 0);
  if (l.prot && rng() < S.protRefuse / 100) return { em: N + 9, oneT: 0 };
  return { em, oneT: S.cspCost };
}

function simulate(raw, draws = DRAWS) {
  const S = normalize(raw);
  // Une créance marquée origin "budget" est du run déjà facturé avant août : elle rentre comme
  // du run, pas comme du recouvrement. Même tirage, autre ligne dans le tableau des flux.
  const creances = S.lines.filter((l) => l.type === "creance" && l.origin !== "budget");
  const billed = S.lines.filter((l) => l.type === "creance" && l.origin === "budget");
  const shotLines = S.lines.filter((l) => l.type === "shot" && !l.off); // décoché = le risque ne joue pas
  const budget = S.lines.filter((l) => l.type === "budget");
  const pipe = S.lines.filter((l) => l.type === "pipe");
  const cdis = S.lines.filter((l) => l.type === "cdi");
  // `carried` = les gens d'une autre BU que nos entités paient. Pas de scénario de sortie, mais
  // c'est du cash qui part de nos comptes : ça compte dans la paie comme le reste.
  const others = S.lines.filter((l) => l.type === "stage" || l.type === "carried");
  // Les freelances sont payés le 2e lundi du mois SUIVANT : la prestation de novembre sort en
  // décembre. La ligne reste écrite au mois presté (c'est comme ça qu'on la lit), le décalage se
  // fait ici. Le premier mois de la fenêtre ne prend rien : la prestation qui le précède est déjà
  // portée par les arriérés, la compter ici la paierait deux fois.
  const freelances = S.lines.filter((l) => l.type === "freelance");
  const fixed = S.lines.filter((l) => l.type === "fix");
  // Charges fiscales et arriérés : des sorties certaines, sans proba — la TVA tombe, les
  // paiements en retard aussi. Le financement garde son signe (+ = déblocage, − = échéance).
  const taxes = S.lines.filter((l) => l.type === "tax");
  const arrears = S.lines.filter((l) => l.type === "arrear");
  const loans = S.lines.filter((l) => l.type === "loan");
  const rng = makeRng(98765); // fixed seed: same inputs = same verdict
  const paths = [],
    ends = [],
    breaks = [];
  // Flux moyens sur tous les tirages : ce qui rentre et ce qui sort chaque mois. Moyenne et non
  // médiane, pour que les événements à proba (créances, deals, contentieux) comptent à leur poids.
  const acc = {
    in: new Array(N).fill(0),
    inCre: new Array(N).fill(0),
    inBud: new Array(N).fill(0),
    inPipe: new Array(N).fill(0),
    pay: new Array(N).fill(0),
    fix: new Array(N).fill(0),
    tax: new Array(N).fill(0),
    arrear: new Array(N).fill(0),
    loan: new Array(N).fill(0),
    shot: new Array(N).fill(0),
  };
  for (let d = 0; d < draws; d++) {
    // Créances et contentieux suivent la même règle : la ligne tombe entière ou pas du tout,
    // au mois écrit, avec un mois de retard une fois sur trois.
    const drawLines = (list, out) =>
      list.forEach((l) => {
        if (rng() >= l.p / 100) return;
        const slip = rng() < 0.35 ? 1 : 0;
        KEYS.forEach((k, m) => {
          if (l.months[k]) out[Math.min(N - 1, m + slip)] += l.months[k];
        });
      });
    const shots = new Array(N).fill(0);
    drawLines(shotLines, shots);
    const arIn = new Array(N).fill(0);
    drawLines(creances, arIn);
    const bIn = new Array(N).fill(0); // run facturé avant la fenêtre : la file d'encaissement démarre avec lui
    drawLines(billed, bIn);
    // Un deal du pipe est gagné ou perdu, une fois pour toutes : gagné, toute sa ligne compte.
    const won = pipe.filter((l) => rng() < l.p / 100);
    const exits = cdis.map((l) => ({ l, ...cdiExit(l, S, rng) }));
    let cash = S.cash0,
      breakM = -1;
    const path = new Array(N);
    const bqB = [0, 0],
      bqP = [0, 0]; // le CA facturé attend son encaissement : 60 % à M+1, 40 % à M+2 — une file par origine (run / deals) pour le tableau des flux
    for (let m = 0; m < N; m++) {
      const revB = budget.reduce((sum, l) => sum + l.months[KEYS[m]], 0);
      const revP = won.reduce((sum, l) => sum + l.months[KEYS[m]], 0);
      const inB = bqB[0],
        inP = bqP[0];
      const inflow = inB + bIn[m] + inP + arIn[m];
      bqB[0] = bqB[1] + revB * 0.6;
      bqB[1] = revB * 0.4;
      bqP[0] = bqP[1] + revP * 0.6;
      bqP[1] = revP * 0.4;
      let pay = others.reduce((sum, l) => sum + l.months[KEYS[m]], 0) + (m > 0 ? freelances.reduce((sum, l) => sum + l.months[KEYS[m - 1]], 0) : 0),
        oneT = 0;
      exits.forEach((e) => {
        if (m < e.em) pay += e.l.months[KEYS[m]];
        if (m === e.em && e.em < N) oneT += e.oneT;
      });
      const fix = fixed.reduce((sum, l) => sum + l.months[KEYS[m]], 0);
      const tax = taxes.reduce((sum, l) => sum + l.months[KEYS[m]], 0);
      const arrear = arrears.reduce((sum, l) => sum + l.months[KEYS[m]], 0);
      const loan = loans.reduce((sum, l) => sum + l.months[KEYS[m]], 0); // signé
      cash += inflow + loan - pay - fix - tax - arrear - oneT - shots[m];
      path[m] = cash;
      acc.in[m] += inflow;
      acc.inCre[m] += arIn[m];
      acc.inBud[m] += inB + bIn[m];
      acc.inPipe[m] += inP;
      acc.pay[m] += pay + oneT;
      acc.fix[m] += fix;
      acc.tax[m] += tax;
      acc.arrear[m] += arrear;
      acc.loan[m] += loan;
      acc.shot[m] += shots[m];
      if (cash < 0 && breakM < 0) breakM = m;
    }
    paths.push(path);
    ends.push(cash);
    breaks.push(breakM);
  }
  const pct = (arr, q) => {
    const s = [...arr].sort((a, b) => a - b);
    return s[Math.floor(q * (s.length - 1))];
  };
  const bands = { p10: [], p25: [], p50: [], p75: [], p90: [] };
  for (let m = 0; m < N; m++) {
    const col = paths.map((p) => p[m]);
    bands.p10.push(pct(col, 0.1));
    bands.p25.push(pct(col, 0.25));
    bands.p50.push(pct(col, 0.5));
    bands.p75.push(pct(col, 0.75));
    bands.p90.push(pct(col, 0.9));
  }
  const surv = ends.filter((e) => e >= 0).length / draws;
  const never = breaks.filter((b) => b < 0).length / draws;
  const bs = breaks.filter((b) => b >= 0).sort((a, b) => a - b);
  const flows = {
    in: acc.in.map((v) => v / draws),
    inCre: acc.inCre.map((v) => v / draws),
    inBud: acc.inBud.map((v) => v / draws),
    inPipe: acc.inPipe.map((v) => v / draws),
    pay: acc.pay.map((v) => v / draws),
    fix: acc.fix.map((v) => v / draws),
    tax: acc.tax.map((v) => v / draws),
    arrear: acc.arrear.map((v) => v / draws),
    loan: acc.loan.map((v) => v / draws),
    shot: acc.shot.map((v) => v / draws),
  };
  flows.net = flows.in.map((v, m) => v + flows.loan[m] - flows.pay[m] - flows.fix[m] - flows.tax[m] - flows.arrear[m] - flows.shot[m]);
  return { bands, flows, surv, never, medBreak: bs.length ? bs[Math.floor(bs.length / 2)] : -1, endMed: pct(ends, 0.5), endP10: pct(ends, 0.1), endP90: pct(ends, 0.9) };
}

/* ---------- render helpers ---------- */
const fmtK = (v) => (v >= 0 ? "" : "−") + Math.abs(Math.round(v)) + " k€";

// Projection fin de mois figée. Trois chiffres : ce qu'on avait projeté (figé), ce qu'on a
// aujourd'hui en banque, et l'écart entre les deux. En début de mois tu Figes avec le chiffre
// que te sort le moteur (ou tu le laisses). En fin de mois tu vois si tu y es.
const ProjectionBar = ({ projection, cashToday, liveEndMed, onFreeze }) => {
  const now = new Date();
  const monthLabel = now.toLocaleDateString("fr-FR", { month: "long", year: "2-digit" });
  const frozenDate = projection ? new Date(projection.frozen_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : null;
  if (!projection) {
    return (
      <div className="mt-3 flex items-center gap-3 text-sm text-gray-500">
        <span>Pas de projection figée pour {monthLabel}.</span>
        <button type="button" onClick={onFreeze} className="px-3 py-1 border border-teal-700 text-teal-700 rounded hover:bg-teal-50">
          Figer la projection ({liveEndMed} k€)
        </button>
      </div>
    );
  }
  const ecart = cashToday - projection.end_med_k;
  const ecartColor = Math.abs(ecart) < 5 ? "text-gray-600" : ecart >= 0 ? "text-green-700" : "text-red-600";
  return (
    <div className="mt-3 flex items-center gap-6 text-sm">
      <div>
        <span className="text-gray-500">Projeté fin {monthLabel}</span>{" "}
        <span className="font-bold tabular-nums text-gray-900">{projection.end_med_k} k€</span>
        <span className="text-xs text-gray-400 ml-1">figé {frozenDate}</span>
      </div>
      <div>
        <span className="text-gray-500">Aujourd'hui en banque</span>{" "}
        <span className="font-bold tabular-nums text-gray-900">{Math.round(cashToday)} k€</span>
      </div>
      <div>
        <span className="text-gray-500">Écart</span>{" "}
        <span className={`font-bold tabular-nums ${ecartColor}`}>
          {ecart >= 0 ? "+" : "−"}
          {Math.abs(Math.round(ecart))} k€
        </span>
      </div>
      <button type="button" onClick={onFreeze} className="ml-auto text-xs underline text-gray-500 hover:text-teal-700">
        Refiger ({liveEndMed} k€)
      </button>
    </div>
  );
};

const Field = ({ label, value, onChange, step = 1, min, max }) => (
  <label className="flex flex-col gap-1 text-xs text-gray-500">
    {label}
    <input
      type="number"
      className="bg-gray-50 border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-teal-600 w-full"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const CellInput = ({ value, onChange, step = 1, min, max, width = "w-20" }) => (
  <input
    type="number"
    className={`bg-gray-50 border border-gray-300 rounded px-1.5 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-teal-600 ${width}`}
    value={value}
    step={step}
    min={min}
    max={max}
    onChange={(e) => onChange(e.target.value)}
  />
);

const Th = ({ children, right }) => (
  <th className={`text-[11px] uppercase tracking-wider text-gray-500 font-medium px-2 py-1.5 border-b border-gray-200 whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);

// La grande table : chaque section est un bloc de lignes sous une rangée d'en-tête grise,
// toutes alignées sur les mêmes 17 colonnes de mois. Colonnes fixes : Ligne · % (proba ou
// contentieux seulement). Les sorties CDI (st/exit) restent dans le plan côté API et jouent
// dans le moteur, mais ne s'éditent plus à l'écran. `rows` porte l'index de chaque ligne
// dans state.lines pour que le filtrage par type ne décale pas les handlers.
// Nombre de colonnes d'un colSpan de titre : variable, l'onglet PNL en a une de plus.
const colsOf = (keys) => 2 + keys.length;

// La fiche accounting derrière une ligne, quand il y en a une : l'onglet Budget du projet pour un
// budget, la fiche de la personne pour un CDI ou un freelance.
const href = (l) => {
  if (l.invoiceId) return `/invoice/${l.invoiceId}/collection`;
  if (l.projectId) return `/project/${l.projectId}/budget`;
  if (l.userId) return `/people?user_modal_id=${l.userId}&user_modal_tab=about`;
  return null;
};
// Les lignes qui savent s'écrire dans la source : budget/pipe (le budget accounting), people
// (les contrats), frais fixes, charges fiscales et financement (la BU), et les créances (ce
// qu'on attend de la facture). Restent des simulations : le contentieux, les arriérés et les
// personnes payées au jour, dont le coût vient du prévisionnel d'activité.
const savable = (l) => l.budgetId || l.fixedCostId || l.taxCostId || l.loanId || l.invoiceId || (l.userId && !l.daily && ["cdi", "freelance", "stage"].includes(l.type));

// Les deux visages d'un budget : lead du projet et account manager, en avatars de 16 px sur la
// ligne même (demande Seb). Pas d'avatar en base → pastille initiale, pour que le nom reste lisible
// au survol dans les deux cas.
const Face = ({ who, role }) =>
  who?.avatar ? (
    <img src={who.avatar} alt={who.name} title={`${role} : ${who.name}`} className="w-4 h-4 rounded-full object-cover inline-block align-middle" />
  ) : who?.name ? (
    <span title={`${role} : ${who.name}`} className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold inline-flex items-center justify-center align-middle">
      {who.name.charAt(0).toUpperCase()}
    </span>
  ) : null;

// Cliquer sur les avatars (ou le « + » fantôme quand personne n'est posé) ouvre la petite modale
// Lead & AM — les gens sont interchangeables, c'est là qu'on remplace quelqu'un qui part.
const Faces = ({ l, onEdit }) => {
  if (!l.projectId) return null;
  const empty = !l.lead && !l.am;
  if (empty && !onEdit) return null;
  const content = empty ? (
    <span className="w-4 h-4 rounded-full border border-dashed border-gray-300 text-gray-300 text-[10px] leading-none inline-flex items-center justify-center align-middle">+</span>
  ) : (
    <>
      <Face who={l.lead} role="Lead" />
      <Face who={l.am} role="AM" />
    </>
  );
  if (!onEdit) return <span className="ml-1 inline-flex items-center gap-0.5 align-middle shrink-0">{content}</span>;
  return (
    <button
      type="button"
      title="Lead & AM du projet — cliquer pour changer"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(l);
      }}
      className="ml-1 inline-flex items-center gap-0.5 align-middle shrink-0 hover:opacity-70"
    >
      {content}
    </button>
  );
};

// La modale Lead & AM : deux sélecteurs, Sauver écrit sur le projet accounting (PUT /project/:id)
// puis la page recharge le plan — même philosophie que les boutons Sauver des lignes.
function FacesModal({ target, onClose, onSaved }) {
  const [lead, setLead] = useState(target.lead?._id ? target.lead : null);
  const [am, setAm] = useState(target.am?._id ? target.am : null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    try {
      setSaving(true);
      const { ok } = await api.put(`/project/${target.projectId}`, {
        lead_id: lead?._id || "",
        lead_name: lead?.name || "",
        lead_avatar: lead?.avatar || "",
        account_manager_id: am?._id || "",
        account_manager_name: am?.name || "",
        account_manager_avatar: am?.avatar || "",
      });
      if (!ok) return toast.error("Sauvegarde impossible");
      toast.success("Lead & AM enregistrés");
      onSaved();
    } catch (e) {
      toast.error("Sauvegarde impossible");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-white rounded-lg shadow-xl p-4 w-[360px]" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-bold text-gray-900 mb-3 truncate">{target.projectName}</p>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500">Lead</label>
            <SelectUser value={lead} onChange={(u) => setLead(u || null)} userRoles={["super_admin", "admin", "user"]} placeholder="Lead…" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500">Account manager</label>
            <SelectUser value={am} onChange={(u) => setAm(u || null)} userRoles={["super_admin", "admin", "user"]} placeholder="AM…" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
            Annuler
          </button>
          <button type="button" disabled={saving} onClick={save} className="text-sm px-3 py-1.5 rounded bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50">
            Sauver
          </button>
        </div>
      </div>
    </div>
  );
}

// Une ligne du plan : son nom, ses colonnes fixes, une case éditable par mois. `child` la décale
// vers la droite — c'est un budget replié sous sa ligne projet. `orig` est la même ligne dans le
// plan servi par l'API : l'écart avec elle fait apparaître le bouton Sauver.
function LineRow({ l, i, orig, up, onSave, withProba, withCheck, child, onFaces }) {
  const { keys, months } = useCols();
  const dirty =
    orig &&
    (keys.some((k) => num(l.months[k]) !== num(orig.months?.[k])) ||
      ((l.type === "pipe" || l.type === "creance") && num(l.p) !== num(orig.p)) ||
      // décocher une créance l'abandonne : c'est une écriture, pas une simulation
      (l.type === "creance" && !!l.off !== !!orig.off));
  return (
    <tr className={`border-b border-gray-100 ${l.off ? "opacity-40" : ""}`}>
      <td className={`px-2 py-1 whitespace-nowrap sticky left-0 bg-white ${child ? "pl-7" : ""}`}>
        {withCheck ? (
          <input type="checkbox" className="mr-2 align-middle accent-teal-700" checked={!l.off} onChange={(e) => up((s) => (s.lines[i].off = !e.target.checked))} />
        ) : null}
        {/* Ligne rattachée à une fiche accounting (un budget, une personne) : on clique sur le nom
                pour l'ouvrir. Un frais fixe garde son nom (seuls ses mois se sauvent). Les lignes
                sans fiche (créances, contentieux) restent un champ. */}
        {l.fixedCostId ? (
          <span className="inline-block truncate align-middle text-sm w-52" title={l.subCategory}>
            {l.name}
          </span>
        ) : href(l) ? (
          <a
            href={href(l)}
            target="_blank"
            rel="noreferrer"
            title={l.projectId ? "Ouvrir le budget dans accounting" : "Ouvrir la fiche dans accounting"}
            className={`inline-block truncate align-middle text-sm underline decoration-dotted underline-offset-2 text-teal-700 hover:text-teal-900 ${child ? "w-44" : "w-48"}`}
          >
            {l.name}
          </a>
        ) : (
          <input
            type="text"
            className="bg-transparent border-b border-dashed border-gray-300 text-sm focus:outline-none focus:border-teal-600 w-52"
            value={l.name}
            onChange={(e) => up((s) => (s.lines[i].name = e.target.value))}
          />
        )}
        {l.invoiceId ? null : <Faces l={l} onEdit={onFaces} />}
        {l.prot ? <span className="ml-1 inline-block text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 uppercase">Protégé</span> : null}
        {/* Une créance porte son retard et où en est la relance : la proba se juge avec ça sous les yeux. */}
        {l.invoiceId ? <LateBadge days={l.daysLate} status={l.collectionStatus} /> : null}
        {l.likelyPaid ? <PaidBadge info={l.likelyPaid} /> : null}
        {/* Cette personne a aussi une ligne dans les arriérés. Ce n'est pas le même argent —
            les arriérés s'arrêtent où la fenêtre commence — mais deux fois le même nom sans
            explication, ça se lit comme un doublon. */}
        {l.arrearDue ? (
          <span
            title={`${Math.round(l.arrearDue)} k€ dus avant ${months[0]}, comptés dans la section Arriérés — pas ici`}
            className="ml-1.5 align-middle text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 whitespace-nowrap"
          >
            + {Math.round(l.arrearDue)}k d&apos;arriéré
          </span>
        ) : null}
        {dirty && savable(l) ? (
          <button
            type="button"
            title={saveTarget(l)}
            onClick={() => onSave(l, orig)}
            className="ml-1.5 align-middle text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-700 text-white hover:bg-teal-800"
          >
            Sauver
          </button>
        ) : null}
      </td>
      <td className="px-1 py-1">
        {withProba ? (
          <span className="inline-flex items-center gap-0.5">
            <CellInput value={l.p} step={5} min={0} max={100} width="w-16" onChange={(v) => up((s) => (s.lines[i].p = v))} />
            {/* Une proba jamais jugée est un défaut d'ancienneté, pas une décision : elle le dit. */}
            {l.guessed ? (
              <span title="Proba par défaut, déduite de l'ancienneté — personne n'a encore jugé cette créance" className="text-amber-500 text-xs font-bold leading-none">
                ?
              </span>
            ) : null}
          </span>
        ) : null}
      </td>
      {keys.map((k) => (
        <td key={k} className="px-0.5 py-1">
          <input
            type="number"
            className="bg-gray-50 border border-gray-200 rounded px-1 py-1 text-xs tabular-nums w-14 text-right focus:outline-none focus:ring-2 focus:ring-teal-600"
            value={l.months[k] ?? ""}
            onChange={(e) => up((s) => (s.lines[i].months[k] = e.target.value))}
          />
        </td>
      ))}
    </tr>
  );
}

// Ce que le bouton Sauver va écrire — le titre doit dire dans quoi, l'écriture n'est pas anodine.
const saveTarget = (l) => {
  if (l.budgetId) return "Écrire dans le budget accounting";
  if (l.fixedCostId) return "Écrire dans les frais fixes de la BU";
  if (l.taxCostId) return "Écrire dans les charges fiscales de la BU";
  if (l.loanId) return "Écrire dans l'échéancier de financement de la BU";
  if (l.invoiceId) return "Écrire ce qu'on attend de la facture (montant, mois, proba)";
  return "Écrire dans les contrats accounting";
};

// Le retard d'une créance et l'état de sa relance, à côté de son nom. Même échelle que
// /cashout/recouvrement : gris pas échu, jaune 1-30 j, orange 31-60 j, rouge au-delà.
const LateBadge = ({ days, status }) => {
  const d = num(days);
  const tone = d <= 0 ? "bg-gray-100 text-gray-600" : d <= 30 ? "bg-yellow-100 text-yellow-800" : d <= 60 ? "bg-orange-100 text-orange-800" : "bg-red-100 text-red-700";
  return (
    <span className="ml-1 inline-flex items-center gap-1 align-middle">
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${tone}`}>{d > 0 ? `${d}j` : "à échoir"}</span>
      {status ? <span className="text-[10px] text-gray-400 truncate max-w-[110px]">{status}</span> : null}
    </span>
  );
};

/* ---------- repli par projet (sections budget et pipe) ----------
   Un projet qui porte plusieurs budgets tient sur une ligne de total, ouverte au clic ; un projet
   qui n'en porte qu'un reste une ligne simple, sans flèche. Les cases du projet sont en lecture :
   une saisie sur le total n'aurait pas de règle de répartition entre ses budgets. */
function groupByProject(rows) {
  const groups = [];
  const byId = {};
  for (const row of rows) {
    // `groupId` quand la ligne dit elle-même par quoi elle se regroupe (les créances : par
    // client), sinon le projet — le cas des budgets et du pipe.
    const id = row.l.groupId || row.l.projectId;
    if (!id) {
      groups.push({ single: row });
      continue;
    }
    if (!byId[id]) {
      byId[id] = { id, name: row.l.projectName || row.l.name, rows: [] };
      groups.push(byId[id]);
    }
    byId[id].rows.push(row);
  }
  return groups.map((g) => (g.single || g.rows.length > 1 ? g : { single: g.rows[0] }));
}

// Un virement du montant exact est tombé : l'argent est déjà dans le cash de départ. La ligne
// reste visible — il faut aller pointer la facture — mais elle n'attend plus rien, sinon la
// somme serait comptée deux fois. Le virement est nommé et daté : c'est un signalement à
// vérifier, pas un pointage automatique.
const PaidBadge = ({ info }) => (
  <span
    title={`Virement de ${info.amount} k€ le ${new Date(info.at).toLocaleDateString("fr-FR")} — ${info.label || "?"} (${info.source}). Déjà dans le cash : à pointer comme reçue.`}
    className="ml-1.5 align-middle text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 whitespace-nowrap"
  >
    probablement encaissée · {new Date(info.at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
  </span>
);

function ProjectRow({ group, open, onToggle, onFaces }) {
  const { keys } = useCols();
  const total = (k) => group.rows.reduce((sum, { l }) => sum + num(l.months[k]), 0);
  // Proba du groupe : la moyenne pondérée par les montants, pas la moyenne des pourcentages —
  // une facture de 55k à 25 % et une de 2k à 90 % ne pèsent pas pareil. Replié, un client doit
  // dire ce qu'on pense en encaisser ; sinon on ne voit plus que la proba a été jugée.
  const lineTotal = (l) => keys.reduce((s, k) => s + num(l.months[k]), 0);
  const gross = group.rows.reduce((s, { l }) => s + lineTotal(l), 0);
  const weighted = group.rows.reduce((s, { l }) => s + lineTotal(l) * (num(l.p) / 100), 0);
  const hasProba = group.rows.some(({ l }) => l.p != null);
  const anyGuessed = group.rows.some(({ l }) => l.guessed);
  return (
    <tr className="border-b border-gray-100 bg-gray-50/60">
      <td className="px-2 py-1 whitespace-nowrap sticky left-0 bg-gray-50">
        <span className="inline-flex items-center gap-1 w-52">
          <button type="button" onClick={onToggle} className="inline-flex items-center gap-1.5 flex-1 min-w-0 text-left text-sm text-gray-800 hover:text-teal-700">
            <span className="text-gray-400 w-3 shrink-0">{open ? "▾" : "▸"}</span>
            <span className="truncate">{group.name}</span>
          </button>
          <Faces l={group.rows[0].l} onEdit={onFaces} />
          <span className="ml-auto shrink-0 text-[10px] text-gray-500 tabular-nums">{group.rows.length}</span>
        </span>
      </td>
      <td className="px-1 py-1 text-right tabular-nums text-xs text-gray-500">
        {hasProba && gross > 0 ? (
          <span className={anyGuessed ? "text-amber-600" : ""} title={anyGuessed ? "au moins une proba jamais jugée" : ""}>
            {Math.round((weighted / gross) * 100)}%{anyGuessed ? " ?" : ""}
          </span>
        ) : null}
      </td>
      {keys.map((k) => (
        <td key={k} className={`px-1 py-1 text-right tabular-nums text-xs ${Math.round(total(k)) ? "text-gray-900" : "text-gray-300"}`}>
          {Math.round(total(k)) || "·"}
        </td>
      ))}
    </tr>
  );
}

// TACE mensuel — lecture seule, mesuré côté API depuis le prévisionnel d'activité et le roster
// facturable de la BU (la logique de la page /tace, ramenée au mois). Trois lignes : l'occupation
// signée, l'occupation pondérée par la confiance des budgets, et les jours restant à vendre pour
// atteindre la cible. Un mois sans prévisionnel saisi affiche « · » : pas de donnée, pas un zéro.
const TACE_TARGET = 80; // % — la même cible que la page /tace
const taceColor = (pct) => (pct >= 80 ? "text-green-700" : pct >= 60 ? "text-green-600" : pct >= 40 ? "text-amber-600" : "text-red-600");

function TaceRows({ tace }) {
  const { keys } = useCols();
  if (!tace) return null;
  const cell = "px-1 py-1 text-right tabular-nums text-xs whitespace-nowrap";
  const pct = (k, hours) => (tace[k]?.cap > 0 ? Math.round((hours / tace[k].cap) * 100) : 0);
  const hasData = (k) => tace[k] && tace[k].cap > 0 && tace[k].brut > 0;
  const rows = [
    { label: "TACE signé", value: (k) => `${pct(k, tace[k].signed)}%`, cls: (k) => taceColor(pct(k, tace[k].signed)) },
    { label: "TACE pondéré", value: (k) => `${pct(k, tace[k].weighted)}%`, cls: (k) => taceColor(pct(k, tace[k].weighted)) },
    {
      label: `Jours à vendre (cible ${TACE_TARGET} %)`,
      value: (k) => `${Math.max(0, Math.round(((TACE_TARGET / 100) * tace[k].cap - tace[k].weighted) / 8))}j`,
      cls: (k) => (Math.round(((TACE_TARGET / 100) * tace[k].cap - tace[k].weighted) / 8) > 0 ? "text-red-600 font-semibold" : "text-gray-500"),
    },
  ];
  return (
    <>
      <tr>
        <td colSpan={colsOf(keys)} className="h-5" />
      </tr>
      <tr className="bg-gray-100">
        <td colSpan={colsOf(keys)} className="px-2 py-1.5 text-[11px] font-bold tracking-[.14em] uppercase text-gray-600 whitespace-nowrap sticky left-0">
          TACE — occupation depuis le prévisionnel d'activité ·{" "}
          <a
            href="/tace"
            target="_blank"
            rel="noreferrer"
            className="underline decoration-dotted underline-offset-2 text-teal-700 hover:text-teal-900 normal-case tracking-normal font-medium"
          >
            détail par personne
          </a>
        </td>
      </tr>
      {rows.map((r) => (
        <tr key={r.label} className="border-b border-gray-100">
          <td colSpan={2} className="px-2 py-1 whitespace-nowrap sticky left-0 bg-white text-sm">
            {r.label}
          </td>
          {keys.map((k) => (
            <td key={k} className={`${cell} ${hasData(k) ? r.cls(k) : "text-gray-300"}`}>
              {hasData(k) ? r.value(k) : "\u00b7"}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// D'où vient une section, et de quand elle date. Sans ça, une section live et une section saisie
// à la main six semaines plus tôt se lisaient exactement pareil — c'est comme ça que la page a pu
// afficher 97 k€ de cash pendant que le compte était à 25 k€, sans que rien ne cloche à l'écran.
const Provenance = ({ source }) => {
  if (!source) return null;
  const live = source.kind === "live";
  const stamp = source.at ? new Date(source.at) : null;
  const fresh = stamp
    ? Date.now() - stamp.getTime() < 36e5
      ? stamp.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
      : stamp.toLocaleDateString("fr-FR")
    : null;
  return (
    <span className={`ml-2 normal-case tracking-normal font-medium text-[10px] ${live ? "text-teal-700" : "text-amber-700"}`}>
      {live ? "● live" : "○ saisi"} · {source.label}
      {fresh ? ` · ${fresh}` : ""}
    </span>
  );
};

// Le détail du cash de départ, par entité puis par banque. Un total unique laissait croire que
// l'argent est fongible : il ne l'est pas, Le Collectif ne paie pas avec le compte de Selego BV.
// Un compte illisible s'affiche « ? », jamais 0 — une connexion cassée ne doit pas se lire
// comme un compte vide. Un compte hors total (autre société) est grisé et marqué.
function CashAccounts({ accounts }) {
  if (!accounts?.length) return null;

  const byEntity = [];
  for (const a of accounts) {
    let group = byEntity.find((g) => g.entity === a.entity);
    if (!group) byEntity.push((group = { entity: a.entity, counted: a.counted, accounts: [] }));
    group.accounts.push(a);
  }

  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-1 pb-1">
      {byEntity.map((g) => {
        const known = g.accounts.filter((a) => a.amount != null);
        const missing = g.accounts.filter((a) => a.amount == null);
        const sum = known.reduce((s, a) => s + num(a.amount), 0);
        // Les banques ne sont pas relues au même rythme : Qonto toutes les 5 min, BNP 2x/jour.
        // On ne signale que ce qui dort depuis plus d'un jour — sinon le bruit noie l'info.
        const stale = known.filter((a) => a.at && Date.now() - new Date(a.at) > 36 * 3600 * 1000);
        return (
          <div key={g.entity} className={`text-[11px] leading-tight ${g.counted ? "text-gray-700" : "text-gray-400"}`}>
            <div className="font-medium whitespace-nowrap">
              {g.entity}
              {g.counted ? "" : " · hors total"}
            </div>
            <div className="tabular-nums whitespace-nowrap">
              {known.length ? `${Math.round(sum)} k€` : "?"}
              <span className="text-gray-400"> · {known.map((a) => a.bank).join(" + ") || "—"}</span>
              {missing.length ? <span className="text-amber-700"> · {missing.map((a) => a.bank).join(", ")} illisible</span> : null}
              {stale.length ? (
                <span className="text-amber-700" title="solde plus rafraîchi depuis plus d'un jour">
                  {" "}
                  · {stale.map((a) => `${a.bank} au ${new Date(a.at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}`).join(", ")}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SectionRows({ title, source, rows, up, onSave, withProba, withCheck, grouped, onFaces, startCollapsed = false }) {
  const { keys } = useCols();
  const [open, setOpen] = useState(() => new Set());
  // Section repliée : on garde l'en-tête et le total, on cache les lignes. En Tréso, le carnet
  // signé et le pipe ne sont pas des leviers du mois — c'est la facturation à venir, elle joue
  // dans le moteur mais elle n'a pas à occuper trente lignes entre le cash et la paie.
  const [collapsed, setCollapsed] = useState(startCollapsed);
  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const groups = grouped ? groupByProject(rows) : rows.map((row) => ({ single: row }));
  const props = { up, onSave, withProba, withCheck, onFaces };

  return (
    <>
      {/* Respiration avant chaque titre : les sections se lisent comme des blocs séparés. */}
      <tr>
        <td colSpan={colsOf(keys)} className="h-5" />
      </tr>
      <tr className="bg-gray-100">
        <td colSpan={colsOf(keys)} className="px-2 py-1.5 text-[11px] font-bold tracking-[.14em] uppercase text-gray-600 whitespace-nowrap sticky left-0">
          <button type="button" onClick={() => setCollapsed((c) => !c)} className="text-left hover:text-teal-700">
            <span className="text-gray-400 mr-1 font-normal">{collapsed ? "▸" : "▾"}</span>
            {title}
            {collapsed ? <span className="ml-1.5 normal-case tracking-normal font-medium text-[10px] text-gray-400">({rows.length} lignes repliées)</span> : null}
          </button>
          <Provenance source={source} />
        </td>
      </tr>
      {collapsed
        ? null
        : groups.map((g) =>
            g.single ? (
              <LineRow key={g.single.i} l={g.single.l} i={g.single.i} orig={g.single.orig} {...props} />
            ) : (
              <React.Fragment key={g.id}>
                <ProjectRow group={g} open={open.has(g.id)} onToggle={() => toggle(g.id)} onFaces={onFaces} />
                {open.has(g.id) ? g.rows.map(({ l, i, orig }) => <LineRow key={i} l={l} i={i} orig={orig} {...props} child />) : null}
              </React.Fragment>
            ),
          )}
      <tr className="font-semibold text-gray-700 border-b border-gray-200">
        <td colSpan={2} className="px-2 py-1.5 sticky left-0 bg-white">
          Total
        </td>
        {keys.map((k) => (
          <td key={k} className="px-1 py-1.5 text-right tabular-nums text-xs">
            {Math.round(rows.reduce((sum, { l }) => sum + num(l.months[k]), 0))}
          </td>
        ))}
      </tr>
    </>
  );
}

// Les courbes du graphe. Chaque série lit `res` : la médiane du cash, les flux moyens du mois.
// L'éventail P10-P90 est une série comme les autres, on peut le masquer.
const SERIES = [
  { key: "cash", label: "Cash (médiane)", color: COLORS.accent, width: 2.6, get: (r) => r.bands.p50 },
  { key: "band", label: "Éventail P10–P90", color: COLORS.accent, band: true },
  { key: "in", label: "Encaissé", color: COLORS.good, get: (r) => r.flows.in },
  { key: "pay", label: "Paie", color: COLORS.bad, get: (r) => r.flows.pay },
  { key: "fix", label: "Frais fixes", color: COLORS.warn, get: (r) => r.flows.fix },
  { key: "shot", label: "Contentieux", color: COLORS.violet, get: (r) => r.flows.shot },
  { key: "net", label: "Net du mois", color: COLORS.muted, dash: [5, 4], get: (r) => r.flows.net },
];
const HIDDEN_AT_START = ["fix", "shot"];

function Chart({ res }) {
  const ref = useRef(null);
  const [hidden, setHidden] = useState(() => new Set(HIDDEN_AT_START));
  const [width, setWidth] = useState(1040);
  const [hover, setHover] = useState(null); // index du mois sous la souris

  const toggle = (key) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Le canvas suit la largeur réelle du bloc : sinon il est étiré en CSS et tout est flou.
  useEffect(() => {
    const cv = ref.current;
    if (!cv?.parentElement) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(cv.parentElement);
    return () => ro.disconnect();
  }, []);

  const shown = SERIES.filter((s) => !hidden.has(s.key));

  useEffect(() => {
    const cv = ref.current;
    if (!cv || !res) return;
    const dpr = window.devicePixelRatio || 1;
    const H = 380;
    cv.width = width * dpr;
    cv.height = H * dpr;
    cv.style.width = width + "px";
    cv.style.height = H + "px";
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const padL = 58,
      padR = 16,
      padT = 16,
      padB = 40;
    ctx.clearRect(0, 0, width, H);

    // Échelle : uniquement sur les séries visibles, pour qu'en masquant le cash on zoome sur les flux.
    const vals = [0];
    shown.forEach((s) => (s.band ? vals.push(...res.bands.p10, ...res.bands.p90) : vals.push(...s.get(res))));
    let lo = Math.min(...vals),
      hi = Math.max(...vals);
    if (hi - lo < 20) hi = lo + 20;
    const pad = (hi - lo) * 0.1;
    lo -= pad;
    hi += pad;
    const X = (i) => padL + ((width - padL - padR) * i) / (N - 1);
    const Y = (v) => padT + (H - padT - padB) * (1 - (v - lo) / (hi - lo));

    // grille + axe des montants
    const raw = (hi - lo) / 6;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const step = Math.max(10, Math.ceil(raw / mag) * mag);
    ctx.font = "11px -apple-system,Segoe UI,Roboto,sans-serif";
    for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) {
      ctx.strokeStyle = Math.abs(v) < 0.01 ? COLORS.bad : COLORS.line;
      ctx.lineWidth = 1;
      ctx.setLineDash(Math.abs(v) < 0.01 ? [2, 3] : []);
      ctx.beginPath();
      ctx.moveTo(padL, Y(v));
      ctx.lineTo(width - padR, Y(v));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = COLORS.muted;
      ctx.textAlign = "right";
      ctx.fillText(Math.round(v) + "k", padL - 6, Y(v) + 4);
    }
    // zone négative
    if (lo < 0) {
      ctx.fillStyle = "rgba(198,40,40,.06)";
      ctx.fillRect(padL, Y(0), width - padL - padR, Y(lo) - Y(0));
    }

    // mois survolé
    if (hover != null) {
      ctx.strokeStyle = COLORS.muted;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(X(hover), padT);
      ctx.lineTo(X(hover), H - padB);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const line = (data, color, w, dash) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = w || 1.8;
      ctx.setLineDash(dash || []);
      ctx.beginPath();
      data.forEach((v, i) => (i ? ctx.lineTo(X(i), Y(v)) : ctx.moveTo(X(0), Y(v))));
      ctx.stroke();
      ctx.setLineDash([]);
    };

    shown.forEach((s) => {
      if (!s.band) return;
      ctx.beginPath();
      res.bands.p10.forEach((v, i) => (i ? ctx.lineTo(X(i), Y(v)) : ctx.moveTo(X(0), Y(v))));
      for (let i = N - 1; i >= 0; i--) ctx.lineTo(X(i), Y(res.bands.p90[i]));
      ctx.closePath();
      ctx.fillStyle = s.color;
      ctx.globalAlpha = 0.12;
      ctx.fill();
      ctx.globalAlpha = 1;
    });
    shown.filter((s) => !s.band).forEach((s) => line(s.get(res), s.color, s.width, s.dash));

    // points sur le mois survolé
    if (hover != null) {
      shown
        .filter((s) => !s.band)
        .forEach((s) => {
          ctx.fillStyle = s.color;
          ctx.beginPath();
          ctx.arc(X(hover), Y(s.get(res)[hover]), 3.5, 0, 7);
          ctx.fill();
        });
    }

    // mois en abscisse — tous, inclinés si la place manque
    ctx.fillStyle = COLORS.muted;
    ctx.font = "10px -apple-system,Segoe UI,Roboto,sans-serif";
    const tight = (width - padL - padR) / N < 52;
    MONTHS.forEach((mo, i) => {
      if (tight && i % 2) return;
      ctx.textAlign = "center";
      ctx.fillStyle = i === hover ? COLORS.accent : COLORS.muted;
      ctx.fillText(mo, X(i), H - 22);
    });
  }, [res, width, hidden, hover]);

  const onMove = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const padL = 58,
      padR = 16;
    const i = Math.round(((e.clientX - box.left - padL) / (box.width - padL - padR)) * (N - 1));
    setHover(i >= 0 && i < N ? i : null);
  };

  return (
    <div>
      <canvas ref={ref} className="block" aria-label="Trésorerie et flux mensuels" onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
      <div className="flex gap-1.5 flex-wrap mt-3">
        {SERIES.map((s) => {
          const off = hidden.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              title={off ? "Afficher" : "Masquer"}
              className={`inline-flex items-center gap-1.5 border rounded-full pl-1.5 pr-2.5 py-1 text-xs ${off ? "border-gray-200 bg-white text-gray-400" : "border-gray-300 bg-gray-50 text-gray-800 font-medium"}`}
            >
              <span
                className="w-3 h-3 rounded-sm shrink-0"
                style={{ background: off ? "transparent" : s.color, border: `1.5px solid ${off ? "#D1D5DB" : s.color}`, opacity: s.band && !off ? 0.35 : 1 }}
              />
              {s.label}
              {!off && hover != null && !s.band ? <span className="tabular-nums text-gray-500">{Math.round(s.get(res)[hover])}k</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Le bas de l'onglet Tréso : les mêmes moyennes de tirages que le graphe. C'est de l'encaissé
// (créances tirées, CA run et deals encaissés 60/40), pas la somme des cases facturées
// au-dessus — l'écart entre les deux est le délai d'encaissement. Tréso = médiane.
function FlowRows({ res }) {
  const f = res.flows;
  const lines = [
    { label: "+ Créances", v: f.inCre, cls: "text-green-700" },
    { label: "+ Run (budgets)", v: f.inBud, cls: "text-green-700" },
    { label: "+ Sales (deals)", v: f.inPipe, cls: "text-green-700" },
    { label: "− Paie", v: f.pay, cls: "text-red-700" },
    { label: "− Frais fixes", v: f.fix, cls: "text-red-700" },
    { label: "− Charges fiscales", v: f.tax, cls: "text-red-700" },
    { label: "− Arriérés", v: f.arrear, cls: "text-red-700" },
    { label: "− Contentieux", v: f.shot, cls: "text-red-700" },
    { label: "± Financement", v: f.loan, cls: "text-gray-700" },
  ];
  const cell = "px-1 py-1 text-right tabular-nums text-xs whitespace-nowrap";
  return (
    <>
      <tr>
        {/* Tréso seulement : les flux sortent du moteur, donc toujours la fenêtre de cash. */}
        <td colSpan={colsOf(KEYS)} className="h-5" />
      </tr>
      <tr className="bg-gray-100">
        <td colSpan={colsOf(KEYS)} className="px-2 py-1.5 text-[11px] font-bold tracking-[.14em] uppercase text-gray-600 whitespace-nowrap sticky left-0">
          Flux encaissés
        </td>
      </tr>
      {lines.map((r) => (
        <tr key={r.label} className="border-b border-gray-100">
          <td colSpan={2} className={`px-2 py-1 whitespace-nowrap sticky left-0 bg-white ${r.cls}`}>
            {r.label}
          </td>
          {r.v.map((v, m) => (
            <td key={m} className={`${cell} ${Math.round(v) ? "text-gray-900" : "text-gray-300"}`}>
              {Math.round(v)}
            </td>
          ))}
        </tr>
      ))}
      <tr className="border-t-2 border-gray-300 font-semibold">
        <td colSpan={2} className="px-2 py-1 whitespace-nowrap sticky left-0 bg-white">
          = Net du mois
        </td>
        {f.net.map((v, m) => (
          <td key={m} className={`${cell} ${v >= 0 ? "text-green-700" : "text-red-600"}`}>
            {Math.round(v)}
          </td>
        ))}
      </tr>
      <tr className="font-bold">
        <td colSpan={2} className="px-2 py-1 whitespace-nowrap sticky left-0 bg-white">
          Tréso fin de mois
        </td>
        {res.bands.p50.map((v, m) => (
          <td key={m} className={`${cell} ${v >= 0 ? "text-gray-900" : "text-red-600"}`}>
            {Math.round(v)}
          </td>
        ))}
      </tr>
    </>
  );
}

// Le bas de l'onglet PNL : la PERFORMANCE du mois, pas la tréso (demande Seb, 31/08). Tout est lu
// directement dans les cases affichées, sans délai d'encaissement ni glissement : CA du mois
// (run signé + deals pondérés par leur %) moins paie et frais. Les sorties CDI (statuts) ne
// jouent pas ici : la paie est ce que disent les cases.
function PerfRows({ rows }) {
  const { keys } = useCols();
  const sum = (list, m) => list.reduce((total, { l }) => total + num(l.months[keys[m]]), 0);
  const byType = (...types) => rows.filter(({ l }) => types.includes(l.type));
  const budget = byType("budget");
  const pipe = byType("pipe");
  const people = byType("cdi", "freelance", "stage");
  const fixed = byType("fix");

  const caRun = keys.map((k, m) => sum(budget, m));
  const caDeals = keys.map((k, m) => pipe.reduce((total, { l }) => total + (num(l.p) / 100) * num(l.months[k]), 0));
  const paie = keys.map((k, m) => sum(people, m));
  const frais = keys.map((k, m) => sum(fixed, m));
  const marge = keys.map((k, m) => caRun[m] + caDeals[m] - paie[m] - frais[m]);

  const lines = [
    { label: "+ CA run (signé)", v: caRun, cls: "text-green-700" },
    { label: "+ CA deals (pondéré)", v: caDeals, cls: "text-green-700" },
    { label: "− Paie", v: paie, cls: "text-red-700" },
    { label: "− Frais fixes", v: frais, cls: "text-red-700" },
  ];
  const cell = "px-1 py-1 text-right tabular-nums text-xs whitespace-nowrap";
  return (
    <>
      <tr>
        <td colSpan={colsOf(keys)} className="h-5" />
      </tr>
      <tr className="bg-gray-100">
        <td colSpan={colsOf(keys)} className="px-2 py-1.5 text-[11px] font-bold tracking-[.14em] uppercase text-gray-600 whitespace-nowrap sticky left-0">
          Performance du mois — facturé et pondéré, sans délai d'encaissement
        </td>
      </tr>
      {lines.map((r) => (
        <tr key={r.label} className="border-b border-gray-100">
          <td colSpan={2} className={`px-2 py-1 whitespace-nowrap sticky left-0 bg-white ${r.cls}`}>
            {r.label}
          </td>
          {r.v.map((v, m) => (
            <td key={m} className={`${cell} ${Math.round(v) ? "text-gray-900" : "text-gray-300"}`}>
              {Math.round(v)}
            </td>
          ))}
        </tr>
      ))}
      <tr className="border-t-2 border-gray-300 font-semibold">
        <td colSpan={2} className="px-2 py-1 whitespace-nowrap sticky left-0 bg-white">
          = Marge du mois
        </td>
        {marge.map((v, m) => (
          <td key={m} className={`${cell} ${v >= 0 ? "text-green-700" : "text-red-600"}`}>
            {Math.round(v)}
          </td>
        ))}
      </tr>
    </>
  );
}

/* ---------- page ---------- */
export default function Pilotage() {
  // `plan` est le plan tel que servi par l'API — la référence du Δ ; `state` la copie de
  // travail que l'écran édite. Rien à afficher tant que l'API n'a pas répondu.
  const [plan, setPlan] = useState(null);
  const [state, setState] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);
  // La projection fin de mois figée pour la BU courante, lue au chargement. Si personne n'a cliqué
  // Figer ce mois-ci c'est null et on propose de le faire. Un seul chiffre, un par (BU, mois).
  const [projection, setProjection] = useState(null);
  // Deux lectures du même plan : PNL (le facturé et la marge, sans recouvrement) et Tréso
  // (le cash — verdict, graphe, créances, flux encaissés). L'onglet est porté par l'URL, la
  // barre du haut de la section Admin le pilote. La BU vient du sélecteur global du header :
  // "ALL" (ou une BU inconnue de l'API) retombe sur Selego côté serveur.
  const { tab } = useParams();
  const { businessUnit } = useSelector((s) => s.Auth);
  // La modale Lead & AM : la ligne cliquée (projectId + visages actuels), null = fermée.
  const [facesTarget, setFacesTarget] = useState(null);
  const openFaces = (l) => setFacesTarget({ projectId: l.projectId, projectName: l.projectName || l.name, lead: l.lead, am: l.am });

  const res = useMemo(() => (state ? simulate(state) : null), [state]);
  const baseline = useMemo(() => (plan ? simulate(plan) : null), [plan]);

  const fetchPlan = async () => {
    try {
      const { ok, data } = await api.post("/pilotage/data", { businessUnitId: businessUnit?._id });
      if (!ok) return toast.error("Impossible de charger le plan");
      setPlan(data);
      setState(JSON.parse(JSON.stringify(data)));
    } catch (e) {
      toast.error("Impossible de charger le plan");
    }
  };

  const fetchProjection = async () => {
    try {
      const { ok, data } = await api.get(`/pilotage/projection?business_unit_id=${businessUnit?._id || ""}`);
      if (ok) setProjection(data);
    } catch (e) {
      // Pas de projection figée ce mois-ci, pas grave : l'écran l'affiche comme à faire.
    }
  };

  // Écrit la projection fin de mois courant pour la BU courante. On prend la médiane du moteur
  // au premier mois de la fenêtre (= 1er du mois prochain = fin du mois en cours). Un refige
  // écrase. Ce chiffre ne bougera plus de tout le mois.
  const freezeProjection = async () => {
    if (!res) return;
    const end_med_k = Math.round(res.bands.p50[0]);
    if (projection && !window.confirm(`Remplacer la projection figée (${projection.end_med_k} k€) par ${end_med_k} k€ ?`)) return;
    try {
      const { ok, data } = await api.post("/pilotage/projection", {
        business_unit_id: businessUnit?._id || "",
        end_med_k,
      });
      if (!ok) return toast.error("Impossible de figer");
      setProjection(data);
      toast.success(`Projection figée à ${end_med_k} k€`);
    } catch (e) {
      toast.error("Impossible de figer");
    }
  };

  // Changer de BU dans le header recharge tout — les simulations en cours sont perdues.
  useEffect(() => {
    setPlan(null);
    setState(null);
    setProjection(null);
    fetchPlan();
    fetchProjection();
  }, [businessUnit?._id]);

  // clone-and-mutate updater: keeps the many nested field handlers short
  const up = (fn) =>
    setState((prev) => {
      const s = JSON.parse(JSON.stringify(prev));
      fn(s);
      return s;
    });

  // Le bouton Sauver d'une ligne : n'envoie que les mois modifiés (en k€), écrit dans la source
  // (budget ou contrats), puis recharge le plan — ce qui s'affiche redevient le réel. Attention :
  // le rechargement remet aussi à zéro les simulations des autres lignes.
  const saveLine = async (l, orig) => {
    const changes = {};
    // DATA_KEYS : une saisie sur la colonne du mois courant, visible en PNL, doit partir elle aussi.
    DATA_KEYS.forEach((k) => {
      if (num(l.months[k]) !== num(orig.months?.[k])) changes[k] = num(l.months[k]);
    });
    try {
      // Ligne people : chaque mois modifié devient une période de contrat (tarif, arrêt, reprise).
      // Budget/pipe : le phasage (et la confiance) du budget. Frais fixe, charges fiscales et
      // financement : leur phasage sur la BU. Créance : ce qu'on attend de la facture.
      let url = `/pilotage/contract/${l.userId}`;
      if (l.budgetId) url = `/pilotage/budget/${l.budgetId}`;
      if (l.fixedCostId) url = `/pilotage/fixed-cost/${l.fixedCostId}`;
      if (l.taxCostId) url = `/pilotage/tax-cost/${l.taxCostId}`;
      if (l.loanId) url = `/pilotage/loan/${l.loanId}`;
      if (l.invoiceId) url = `/pilotage/invoice/${l.invoiceId}`;
      const body = { months: changes };
      if ((l.type === "pipe" || l.type === "creance") && num(l.p) !== num(orig.p)) body.confidence = num(l.p);
      // Décocher une créance = l'abandonner (written_off) : elle sort du prévisionnel.
      if (l.type === "creance" && !!l.off !== !!orig.off) body.written_off = !!l.off;
      const { ok } = await api.put(url, body);
      if (!ok) return toast.error("Sauvegarde impossible");

      toast.success("Enregistré — le plan recharge le réel");
      fetchPlan();
    } catch (e) {
      toast.error("Sauvegarde impossible");
    }
  };

  if (!res || !baseline) return <div className="p-4 text-sm text-gray-500">Chargement du plan…</div>;

  // Une ligne vide sur toute la fenêtre ne dit rien : quelqu'un qui est parti, un projet sans
  // montant. On la sort — sauf si on doit encore de l'argent à la personne, auquel cas le nom doit
  // rester visible en face de son arriéré. On teste mois par mois plutôt que la somme : +5 puis -5
  // sur deux mois, ce n'est pas une ligne vide. Le filtre suit les colonnes de l'onglet : un deal
  // qui ne porte que le mois courant est une ligne pleine en PNL et une ligne vide en Tréso.
  // Un poste de frais fixes échappe au filtre : il a été créé exprès pour être rempli à la main,
  // et une ligne à zéro qu'on ne voit pas est une ligne qu'on ne pourra jamais remplir.
  const { keys: cols, months: colMonths } = colsFor(tab);
  // Typed-in lines (fixed costs, taxes, financing) stay visible while empty: they are where
  // someone enters the amount, so hiding them until they have one made them impossible to fill.
  const rows = state.lines
    .map((l, i) => ({ l, i, orig: plan.lines[i] }))
    .filter(({ l }) => l.arrearDue || l.fixedCostId || l.taxCostId || l.loanId || cols.some((k) => num(l.months[k])));
  const creanceRows = rows.filter(({ l }) => l.type === "creance");
  const budgetRows = rows.filter(({ l }) => l.type === "budget");
  const pipeRows = rows.filter(({ l }) => l.type === "pipe");
  const peopleRows = rows.filter(({ l }) => l.type === "cdi" || l.type === "stage");
  const freeRows = rows.filter(({ l }) => l.type === "freelance");
  const fixRows = rows.filter(({ l }) => l.type === "fix");
  const taxRows = rows.filter(({ l }) => l.type === "tax");
  const loanRows = rows.filter(({ l }) => l.type === "loan");
  const arrearRows = rows.filter(({ l }) => l.type === "arrear");
  const carriedRows = rows.filter(({ l }) => l.type === "carried");
  const intercoRows = rows.filter(({ l }) => l.type === "interco");
  const shotRows = rows.filter(({ l }) => l.type === "shot");
  const src = state.sources || {};
  const amountOf = ({ l }) => cols.reduce((sum, k) => sum + num(l.months[k]), 0);
  const monthTotal = (list, m) => list.reduce((sum, { l }) => sum + num(l.months[cols[m]]), 0);
  const shotsExposure = shotRows.reduce((sum, r) => sum + (r.l.off ? 0 : (num(r.l.p) / 100) * amountOf(r)), 0);
  const pipeGross = pipeRows.reduce((sum, r) => sum + amountOf(r), 0);
  const pipeWeighted = pipeRows.reduce((sum, r) => sum + (num(r.l.p) / 100) * amountOf(r), 0);
  const creanceGross = creanceRows.reduce((sum, r) => sum + amountOf(r), 0);
  const creanceWeighted = creanceRows.reduce((sum, r) => sum + (num(r.l.p) / 100) * amountOf(r), 0);

  const survColor = res.surv >= 0.7 ? "text-green-700" : res.surv >= 0.4 ? "text-amber-600" : "text-red-600";
  let endBand = `P10 ${fmtK(res.endP10)} · P90 ${fmtK(res.endP90)}`;
  const delta = res.endMed - baseline.endMed;
  if (Math.abs(delta) >= 1) endBand += `  ·  Δ vs plan ${delta > 0 ? "+" : "−"}${Math.abs(Math.round(delta))}k`;

  return (
    <div className="p-4">
      {/* Verdict et graphe (Tréso seulement) : repliés par défaut, la table est ce qu'on vient lire. Replié,
          le bandeau garde les quatre chiffres en une ligne ; ouvert, il les détaille et montre le graphe. */}
      {tab === "treso" ? (
        <div className="sticky top-0 z-20 bg-gray-50 -mx-4 px-4 py-2 border-b border-gray-200">
          <button type="button" onClick={() => setChartOpen(!chartOpen)} className="flex items-center gap-3 flex-wrap w-full text-left">
            <span className="flex items-center gap-1.5 text-[11px] tracking-wider uppercase text-gray-500 hover:text-teal-700">
              <span className="text-gray-400">{chartOpen ? "▾" : "▸"}</span>
              Verdict et graphe
            </span>
            {!chartOpen ? (
              <span className="flex items-center gap-4 text-sm tabular-nums">
                <span className={`font-bold ${survColor}`}>{Math.round(res.surv * 100)} %</span>
                <span className="text-gray-500">vivant fin 27</span>
                <span className="font-bold text-gray-900">{Math.round(res.never * 100)} %</span>
                <span className="text-gray-500">jamais en rupture</span>
                <span className={`font-bold ${res.medBreak < 0 ? "text-green-700" : "text-red-600"}`}>{res.medBreak < 0 ? "jamais" : MONTHS[res.medBreak]}</span>
                <span className="text-gray-500">rupture médiane</span>
                <span className={`font-bold ${res.endMed >= 0 ? "text-green-700" : "text-red-600"}`}>{fmtK(res.endMed)}</span>
                <span className="text-gray-500">en déc-27</span>
              </span>
            ) : null}
          </button>
          {chartOpen ? (
            <div className="flex gap-3 flex-wrap mt-2">
              <div className="flex-1 min-w-[150px] bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
                <p className="text-[11px] tracking-wider uppercase text-gray-500">Finir 2027 vivant</p>
                <p className={`text-3xl font-bold tabular-nums ${survColor}`}>{Math.round(res.surv * 100)} %</p>
                <p className="text-xs text-gray-500">cash ≥ 0 en déc-27</p>
              </div>
              <div className="flex-1 min-w-[150px] bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
                <p className="text-[11px] tracking-wider uppercase text-gray-500">Jamais en rupture</p>
                <p className="text-2xl font-bold tabular-nums text-gray-900">{Math.round(res.never * 100)} %</p>
                <p className="text-xs text-gray-500">cash jamais &lt; 0</p>
              </div>
              <div className="flex-1 min-w-[150px] bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
                <p className="text-[11px] tracking-wider uppercase text-gray-500">Rupture médiane</p>
                <p className={`text-2xl font-bold tabular-nums ${res.medBreak < 0 ? "text-green-700" : "text-red-600"}`}>{res.medBreak < 0 ? "jamais" : MONTHS[res.medBreak]}</p>
                <p className="text-xs text-gray-500">si rupture il y a</p>
              </div>
              <div className="flex-1 min-w-[150px] bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
                <p className="text-[11px] tracking-wider uppercase text-gray-500">Cash déc-27 (médiane)</p>
                <p className={`text-2xl font-bold tabular-nums ${res.endMed >= 0 ? "text-green-700" : "text-red-600"}`}>{fmtK(res.endMed)}</p>
                <p className="text-xs text-gray-500 tabular-nums">{endBand}</p>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "treso" && chartOpen ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4 shadow-sm">
          <Chart res={res} />
        </div>
      ) : null}

      {/* La grande table : les sections partagées, puis le bas propre à l'onglet — performance
          en PNL, recouvrement + flux encaissés en Tréso. */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4 shadow-sm">
        {/* Le cash de départ vient de la banque (Qonto, toutes les 5 min), il ne se saisit plus.
            Il reste modifiable pour simuler — mais l'écran dit d'où vient le vrai chiffre et de
            quand il date, parce que c'est ce point de départ qui fait tout le reste. */}
        {tab === "treso" ? (
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="w-40">
              <Field label="Cash aujourd'hui (k€)" value={state.cash0} onChange={(v) => up((s) => (s.cash0 = v))} />
            </div>
            <p className="text-[11px] text-gray-500 pb-1.5">
              <Provenance source={src.cash0} />
              {num(state.cash0) !== num(plan.cash0) ? <span className="ml-2 text-amber-700">simulation · réel {Math.round(num(plan.cash0))} k€</span> : null}
            </p>
            <CashAccounts accounts={plan.cashAccounts} />
          </div>
        ) : null}
        {/* Projection fin de mois figée. Trois chiffres côte à côte : la projection figée (ne
            bouge plus du mois), le cash d'aujourd'hui (live), et l'écart. Un bouton Figer /
            Refiger qui écrit la médiane du moteur du jour. */}
        {tab === "treso" ? (
          <ProjectionBar projection={projection} cashToday={num(state.cash0)} liveEndMed={res ? Math.round(res.bands.p50[0]) : 0} onFreeze={freezeProjection} />
        ) : null}
        <div className="overflow-x-auto mt-3">
          <ColsContext.Provider value={colsFor(tab)}>
            <table className="text-sm border-collapse w-full">
              <thead>
                <tr>
                  <Th>Ligne</Th>
                  <Th>%</Th>
                  {colMonths.map((m) => (
                    <Th key={m} right>
                      {m}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <SectionRows
                  title={`Budget signé — ${budgetRows.length} clients · ${Math.round(monthTotal(budgetRows, 0))}k en ${colMonths[0]}, ${Math.round(monthTotal(budgetRows, cols.length - 1))}k en ${colMonths[cols.length - 1]}`}
                  source={src.budget}
                  rows={budgetRows}
                  up={up}
                  onSave={saveLine}
                  grouped
                  onFaces={openFaces}
                  startCollapsed={tab === "treso"}
                />
                <SectionRows
                  title={`Sales — ${pipeRows.length} deals · ${Math.round(pipeWeighted)}k pondérés sur ${Math.round(pipeGross)}k`}
                  source={src.pipe}
                  rows={pipeRows}
                  up={up}
                  onSave={saveLine}
                  withProba
                  grouped
                  onFaces={openFaces}
                  startCollapsed={tab === "treso"}
                />
                {/* Peut-on livrer ce qu'on vend ? L'occupation mesurée, juste sous le carnet et le pipe. */}
                {tab === "pnl" ? <TaceRows tace={plan.tace} /> : null}
                <SectionRows
                  title={`People — ${peopleRows.length} personnes · ${Math.round(monthTotal(peopleRows, 0))}k en ${colMonths[0]}`}
                  source={src.people}
                  rows={peopleRows}
                  up={up}
                  onSave={saveLine}
                />
                <SectionRows
                  title={`Freelances — ${freeRows.length} lignes · ${Math.round(monthTotal(freeRows, 0))}k en ${colMonths[0]}`}
                  source={src.people}
                  rows={freeRows}
                  up={up}
                  onSave={saveLine}
                />
                {/* Portés pour d'autres BU : nos entités les paient, une autre BU en profite. Hors
                  du PNL — c'est censé être refacturé, l'y mêler fausserait la marge Selego. En
                  Tréso ça compte plein pot : l'argent sort de nos comptes. */}
                {tab === "treso" && carriedRows.length ? (
                  <SectionRows
                    title={`Portés pour d'autres BU — ${carriedRows.length} personnes · ${Math.round(monthTotal(carriedRows, 0))}k en ${colMonths[0]}, à refacturer`}
                    source={src.carried}
                    rows={carriedRows}
                    up={up}
                  />
                ) : null}
                <SectionRows
                  title={`Frais fixes — ${fixRows.length} postes · ${Math.round(monthTotal(fixRows, 0))}k en ${colMonths[0]}`}
                  source={src.fix}
                  rows={fixRows}
                  up={up}
                  onSave={saveLine}
                />
                {/* Charges fiscales : la TVA et l'IS ne sont dans aucune autre ligne. L'URSSAF si :
                  total_monthly_cost est un coût chargé, la mettre ici la compterait deux fois. */}
                <SectionRows
                  title={`Charges fiscales — TVA et IS · ${Math.round(taxRows.reduce((s, r) => s + amountOf(r), 0))}k sur la fenêtre`}
                  source={src.tax}
                  rows={taxRows}
                  up={up}
                  onSave={saveLine}
                />
                {/* Financement : signé. Positif = déblocage encaissé, négatif = échéance payée. */}
                {tab === "treso" ? (
                  <SectionRows title="Financement — prêts et découverts (+ encaissé, − remboursé)" source={src.loan} rows={loanRows} up={up} onSave={saveLine} />
                ) : null}
                {/* Le contentieux est un risque de sortie de cash, pas une charge du mois : Tréso seulement. */}
                {tab === "treso" ? (
                  <SectionRows
                    title={`Contentieux & redressements — ${shotRows.length} risques · ${Math.round(shotsExposure)} k€ d'exposition pondérée`}
                    source={src.shot}
                    rows={shotRows}
                    up={up}
                    withProba
                    withCheck
                  />
                ) : null}
                {tab === "treso" ? (
                  <SectionRows
                    title={`Recouvrement — ${creanceRows.length} factures · ${Math.round(creanceWeighted)}k pondérés sur ${Math.round(creanceGross)}k${
                      creanceRows.filter(({ l }) => l.likelyPaid).length ? ` · ${creanceRows.filter(({ l }) => l.likelyPaid).length} probablement déjà encaissées, à pointer` : ""
                    }`}
                    source={src.creance}
                    rows={creanceRows}
                    up={up}
                    withProba
                    withCheck
                    grouped
                    onSave={saveLine}
                  />
                ) : null}
                {/* Encaissé pour une autre BU : déjà dans cash0, mais pas à nous. MÉMO — la somme ne
                  descend pas dans le calcul : elle repartira soit en virement, soit en payant les
                  gens qu'on porte pour cette BU, dont le coût est déjà une ligne plus haut. */}
                {tab === "treso" && intercoRows.length ? (
                  <SectionRows
                    title={`Détenu pour d'autres BU — ${Math.round(intercoRows.reduce((s, r) => s + amountOf(r), 0))}k dans nos comptes qui ne sont pas à nous · mémo, hors calcul`}
                    source={src.interco}
                    rows={intercoRows}
                    up={up}
                  />
                ) : null}
                {/* Arriérés : du travail déjà dû, jamais payé. Lecture seule — ça se solde en payant.
                  Repliée par défaut : le total est déjà dans « − Arriérés » du tableau des flux,
                  au-dessus. Ce qu'on garde ici, c'est le seul endroit qui dit À QUI on le doit. */}
                {tab === "treso" && arrearRows.length ? (
                  <SectionRows
                    title={`Arriérés — ${Math.round(arrearRows.reduce((s, r) => s + amountOf(r), 0))}k dus à ${arrearRows.length} personnes, mois antérieurs à ${MONTHS[0]}`}
                    source={src.arrear}
                    rows={arrearRows}
                    up={up}
                    startCollapsed
                  />
                ) : null}
                {tab === "pnl" ? <PerfRows rows={rows} /> : <FlowRows res={res} />}
              </tbody>
            </table>
          </ColsContext.Provider>
        </div>
      </div>
      {facesTarget ? (
        <FacesModal
          target={facesTarget}
          onClose={() => setFacesTarget(null)}
          onSaved={() => {
            setFacesTarget(null);
            fetchPlan();
          }}
        />
      ) : null}
    </div>
  );
}
